<script setup lang="ts">
import type { ContentDetailsAssignment, ContentDetailsTest } from '~/models/Report'
import { useI18n } from 'vue-i18n'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faPencil, faPenRuler, faUserGroup } from '@fortawesome/pro-solid-svg-icons'
import { PendoFeature } from '~/models/Pendo'

const props = defineProps<{
  tasks: readonly ContentDetailsTest[]
  assignments: readonly ContentDetailsAssignment[]
  modelValue?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const { t } = useI18n()

function selectTab(value: string) {
  emit('update:modelValue', value)
}
</script>

<template>
  <div class="mb-6 flex w-full flex-col gap-2 p-1 sm:w-auto sm:flex-row sm:flex-wrap" role="tablist">
    <button
      type="button"
      :data-pendo="PendoFeature.ActivityReports.VisitTab"
      :aria-selected="modelValue === 'main'"
      role="tab"
      class="group flex w-full items-center justify-center rounded-md border px-4 py-2.5 text-sm font-medium transition-all duration-200 focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-offset-2 sm:w-auto sm:justify-start"
      :class="[
        modelValue === 'main'
          ? 'bg-green-40 border-green-40 cursor-default text-white'
          : 'text-green-40 border-green-40 hover:bg-green-40 hover:border-green-40 cursor-pointer bg-white hover:text-white',
      ]"
      @click="selectTab('main')"
    >
      <FontAwesomeIcon
        :icon="faUserGroup"
        class="mr-2"
        :class="[
          modelValue === 'main'
            ? 'text-white'
            : 'text-green-40 group-hover:text-white',
        ]"
      />
      <h3 class="text-sm font-medium">
        {{ t('reports.tabs.visits') }}
      </h3>
    </button>

    <button
      v-for="(test, index) in tasks"
      :key="`task-${test.flytUuid || index}`"
      type="button"
      :data-pendo="PendoFeature.ActivityReports.TaskTab"
      :aria-selected="modelValue === `task-${test.flytUuid || index}`"
      role="tab"
      class="group flex w-full items-center justify-center rounded-md border px-4 py-2.5 text-sm font-medium transition-all duration-200 focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-offset-2 sm:w-auto sm:justify-start"
      :class="[
        modelValue === `task-${test.flytUuid || index}`
          ? 'bg-green-40 border-green-40 cursor-default text-white'
          : 'text-green-40 border-green-40 hover:bg-green-40 hover:border-green-40 cursor-pointer bg-white hover:text-white',
      ]"
      @click="selectTab(`task-${test.flytUuid || index}`)"
    >
      <FontAwesomeIcon
        :icon="faPenRuler"
        class="mr-2"
        :class="[
          modelValue === `task-${test.flytUuid || index}`
            ? 'text-white'
            : 'text-green-40 group-hover:text-white',
        ]"
      />
      <h3 class="text-sm font-medium">
        {{ t('reports.tabs.task') }} {{ index + 1 }}
      </h3>
    </button>

    <button
      v-for="(assignment, index) in assignments"
      :key="`assignment-${assignment.assignmentKey || index}`"
      type="button"
      :data-pendo="PendoFeature.ActivityReports.AssignmentTab"
      :aria-selected="modelValue === `assignment-${assignment.assignmentKey || index}`"
      role="tab"
      class="group flex w-full items-center justify-center rounded-md border px-4 py-2.5 text-sm font-medium transition-all duration-200 focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-offset-2 sm:w-auto sm:justify-start"
      :class="[
        modelValue === `assignment-${assignment.assignmentKey || index}`
          ? 'bg-green-40 border-green-40 cursor-default text-white'
          : 'text-green-40 border-green-40 hover:bg-green-40 hover:border-green-40 cursor-pointer bg-white hover:text-white',
      ]"
      @click="selectTab(`assignment-${assignment.assignmentKey || index}`)"
    >
      <FontAwesomeIcon
        :icon="faPencil"
        class="mr-2"
        :class="[
          modelValue === `assignment-${assignment.assignmentKey || index}`
            ? 'text-white'
            : 'text-green-40 group-hover:text-white',
        ]"
      />
      <h3 class="text-sm font-medium">
        {{ t('reports.tabs.assignment') }} {{ index + 1 }}
      </h3>
    </button>
  </div>
</template>
